import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Listbox, type ListboxItem } from '@/components/ui';
import type { AppRoleOption } from '@/api/console';
import styles from './RolePickerPopover.module.css';

export interface RolePickerPopoverProps {
    appName: string;
    roleKey: string;
    options: AppRoleOption[];
    loading: boolean;
    alreadyLinked: string[];
    onPick: (appRole: string) => void;
    onClose: () => void;
}

/**
 * EAA-1817: "+ vai trò" — picks one of `{app}`'s CATALOG roles to link to a
 * standard role. Options come only from `fetchAppRoles` (already-synced
 * catalog rows); there is deliberately no free-text input, so an admin can
 * never link to an `app_role` that doesn't exist in the app's catalog — the
 * exact mistake `is_stale`/paused rows exist to detect after the fact.
 *
 * A lightweight popover rather than a modal: it is scoped to one table row,
 * needs no focus trap, and Escape / an outside click both simply close it
 * (focus returns to the "+ vai trò" trigger button that opened it).
 */
export function RolePickerPopover({
    appName,
    roleKey,
    options,
    loading,
    alreadyLinked,
    onPick,
    onClose,
}: RolePickerPopoverProps) {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const linkedSet = useMemo(() => new Set(alreadyLinked), [alreadyLinked]);

    useEffect(() => {
        function onPointerDown(event: MouseEvent) {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(event.target as Node)) onClose();
        }
        function onKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') onClose();
        }
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [onClose]);

    const normalizedQuery = query.trim().toLowerCase();
    const filtered = options.filter(
        (option) =>
            !normalizedQuery ||
            `${option.key} ${option.name}`.toLowerCase().includes(normalizedQuery)
    );

    /**
     * EAA-1839: the option rows are the kit's `Listbox` now. This popover was
     * written AFTER `Listbox` shipped and still re-derived the same
     * disabled-with-a-reason row, because the component was missing from the
     * portal's `@/components/ui` barrel — so it was invisible to anyone browsing
     * the kit. `alreadyLinked` becomes the item's `disabled` + `disabledReason`,
     * which is the shape the kit already models.
     */
    const items: ListboxItem<AppRoleOption>[] = filtered.map((option) => ({
        id: option.key,
        data: option,
        disabled: linkedSet.has(option.key),
        disabledReason: t('console.roleCoverage.rolePicker.alreadyAdded'),
    }));

    return (
        <div
            ref={containerRef}
            className={styles.picker}
            role="dialog"
            aria-label={t('console.roleCoverage.rolePicker.dialogAriaLabel', { appName, roleKey })}
        >
            <label className={styles.search}>
                <span className="portal-sr-only">
                    {t('console.roleCoverage.rolePicker.searchSrLabel', { appName })}
                </span>
                {/* EAA-1817: raw <input> → kit Input; `autoFocus` is a plain HTML
                    attribute forwarded straight through to the underlying antd
                    `<input>`, so the "focus the search box on open" behavior this
                    popover relies on (see the file docstring) is unaffected. */}
                <Input
                    autoFocus
                    className={styles.searchInput}
                    placeholder={t('console.roleCoverage.rolePicker.searchPlaceholder', {
                        appName,
                    })}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </label>

            {loading && (
                <p className="portal-admin-hint" role="status">
                    {t('console.roleCoverage.rolePicker.loadingCatalog')}
                </p>
            )}

            {/* EAA-1839: the `<ul>`/`<li>`/`<button>` list is the kit's `Listbox`,
                which also brings the arrow/Home/End/Enter nav this list never had —
                previously the only way through it was Tab, one stop per row.
                `optionsUnstyled` keeps the row chrome in this module's `.option`
                (its `--tint-menu-active` hover, the focus ring, the disabled colour),
                which the kit's inline option palette would otherwise override.

                The empty and the no-match cases are the kit's `emptyState`, so both
                still render inside the one `role="listbox"` container. */}
            {!loading && (
                <Listbox<AppRoleOption>
                    className={styles.list}
                    aria-label={t('console.roleCoverage.rolePicker.listAriaLabel', { appName })}
                    items={items}
                    onSelect={(item) => onPick(item.data.key)}
                    optionsUnstyled
                    optionClassName={() => styles.option}
                    emptyState={
                        <p className={styles.empty}>
                            {t('console.roleCoverage.rolePicker.noMatch')}
                        </p>
                    }
                    renderItem={(item, state) => (
                        <>
                            <span className={styles.optionKey}>{item.data.key}</span>
                            <span className={styles.optionName}>{item.data.name}</span>
                            {state.disabled && (
                                <span className={styles.optionAdded}>{item.disabledReason}</span>
                            )}
                        </>
                    )}
                />
            )}

            <p className={styles.hint}>{t('console.roleCoverage.rolePicker.hint')}</p>
        </div>
    );
}

export default RolePickerPopover;
